(ns hai.tasks.drawer.share
  "The Share tab of the task drawer: who the task is shared with, grouped by
   role under its owner — a saved task's shares, or those a new task stages
   until it is saved."
  (:require
   [clojure.string :as str]
   [hai.entity.related :as related]
   [hai.sharing.components :as sharing.components]
   [hai.sharing.helper :as sharing]
   [hai.sharing.people :as people]
   [hai.tasks.assignee-bubble :as bubble]
   [hai.tasks.data :as data]
   [hai.tasks.urgency-menu :as urgency-menu]
   [hai.ui.components :as c]
   [hai.ui.components.dropdown-menu :as dropdown-menu]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e defevent defevent-fx defsub]]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]))

(def ^:private admin-icon (icons/mui-icon :outlined :group))
(def ^:private viewer-icon (icons/mui-icon :outlined :visibility))
(def ^:private assigned-icon (icons/mui-icon :outlined :check))

;; The roles a share can hold, each by its permission, in the order they are
;; listed.
(def share-roles
  [{:permission :permission.type/admin :heading "Admins" :icon admin-icon}
   {:permission :permission.type/update :heading "Contributors" :icon icons/outlined-edit}
   {:permission :permission.type/read :heading "Viewers" :icon viewer-icon}])

(def ^:private roles
  (into [{:permission people/owner :heading "Owner" :icon icons/crown}]
        share-roles))

;;
;; Styles
;;

(o/defstyled share-rows :div
  {:display "flex" :flex-direction "column" :gap "2px"})

(o/defstyled share-row :div
  {:display "flex" :align-items "center" :gap "8px"
   :min-width "0"
   :padding "2px"
   :border-radius "8px"}
  [:.share-row-name
   {:flex "1" :min-width "0"
    :font-size "12.5px" :font-weight "700"
    :color t/--card-forest
    :overflow "hidden" :text-overflow "ellipsis" :white-space "nowrap"}]
  [:&.flash {:animation "share-row-flash 2.4s ease-out"}])

#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules share-row-keyframes
  (garden.stylesheet/at-keyframes
   :share-row-flash
   ["0%"   {:background "rgba(110,91,204,0.22)"}]
   ["100%" {:background "rgba(110,91,204,0)"}]))

;; Applied by name where the role opens its menu: a Radix trigger needs a plain
;; :button it can put a ref on.
(o/defstyled role-mark :span
  {:flex "none"
   :display "inline-flex" :align-items "center"
   :box-sizing "border-box"
   :height "24px" :padding "0"
   :border "none" :border-radius "999px"
   :background t/--card-surface
   :box-shadow "inset 0 0 0 1px var(--property-3)"
   :font-family "inherit"
   :color t/--card-forest}
  [:&.pressable
   {:cursor "pointer"}
   [#{:&:hover "&[data-state='open']"}
    {:box-shadow "inset 0 0 0 1px var(--card-forest)"}]]
  [:.role-glyph
   {:display "inline-flex" :align-items "center" :justify-content "center"
    :width "24px" :height "24px"}
   [:svg {:width "14px" :height "14px" :fill "currentColor"}]]
  [:.role-assigned
   {:display "inline-flex" :align-items "center" :gap "3px"
    :padding "0 6px 0 8px"
    :font-size "9.5px" :font-weight "800"
    :letter-spacing "0.04em" :text-transform "uppercase"
    :white-space "nowrap"
    :color t/--task-accent}
   [:svg {:width "11px" :height "11px" :fill "currentColor"}]]
  [:&.assigned
   {:padding-right "1px"
    :background t/--tasks
    :box-shadow "inset 0 0 0 1px var(--card-sage)"}
   [:.role-glyph
    {:width "22px" :height "22px"
     :border-radius "50%"
     :background t/--card-surface
     :box-shadow "0 0 0 1px var(--property-3)"}]])

(o/defstyled menu-head :div
  {:display "flex" :flex-direction "column"
   :max-width "240px"
   :padding "4px 12px 6px"
   :line-height "1.25"}
  [#{:.menu-head-name :.menu-head-detail}
   {:overflow "hidden" :text-overflow "ellipsis" :white-space "nowrap"}]
  [:.menu-head-name
   {:font-size "12.5px" :font-weight "800"
    :color t/--card-forest}]
  [:.menu-head-detail
   {:font-size "11px" :font-weight "600"
    :color t/--link-muted}])

;;
;; Who the task is shared with
;;

(defn task-sharing
  "The Share tab on a saved `task`: its rows, how many it is shared with,
   whether `current-user` may change that, and what changes it — each change
   saved at once."
  [task current-user]
  (let [task-uuid (:task/uuid task)]
    {:rows (people/entity-rows task {:current-user-uuid (:user/uuid current-user)
                                     :assignee-uuid (get-in task [:task/assigned-to :user/uuid])})
     :shared-count (count (:entity/shared-with task))
     :can-share? (sharing/can-share? task)
     :on-add (fn [staged]
               (>e [::data/share task-uuid (mapv #(select-keys % [:share :permission]) staged)]))
     :on-role (fn [{:keys [entry]} permission]
                (>e [::data/set-share-permission task-uuid entry permission]))
     :on-remove (fn [{:keys [entry]}]
                  (>e [::data/unshare task-uuid entry]))}))

(defn draft-sharing
  "The Share tab on a new task's `draft`, which `current-user` is making: the
   shares it stages, changed through `change!` and sent once it is saved."
  [{:keys [shares]} change! current-user]
  {:rows (people/draft-rows current-user shares)
   :shared-count (count shares)
   :can-share? true
   :on-add (fn [staged]
             (change! {:shares (into (vec shares) staged)}))
   :on-role (fn [row permission]
              (change! {:shares (people/restage shares (:key row) permission)}))
   :on-remove (fn [row]
                (change! {:shares (people/unstage shares (:key row))}))})

(defn- audience
  "How a share with an audience rather than a person reads: its label, and for
   an email domain what the share covers."
  [scope]
  (case scope
    "*" {:label "Everyone"}
    "published" {:label "Published"}
    "public" {:label "Public"}
    {:label (str "@" scope)
     :hint (str "Anyone with an @" scope " address")}))

(defn- row-label
  [{:keys [person scope]}]
  (if person
    (data/assignee-name person)
    (:label (audience scope))))

(defn- row-order
  [{:keys [person assigned?] :as row}]
  [(some? person)
   (not assigned?)
   (str/lower-case (or (row-label row) ""))])

;; The row to look at, by its key, for as long as it flashes.
(defevent-fx ::flash
  [{:keys [db]} row-key]
  {:db (assoc db ::flashed row-key)
   :dispatch-later {:ms 2400 :dispatch [::flash-shown row-key]}})

(defevent ::flash-shown [db row-key]
  (cond-> db
    (= row-key (::flashed db)) (dissoc ::flashed)))

(defsub ::flashed [db]
  (::flashed db))

;;
;; Views
;;

(defn- role-title
  [{:keys [permission assigned?]}]
  (cond-> (sharing.components/permission-label permission)
    assigned? (str " · assigned")))

(defn- role-glyphs
  [{:keys [assigned?]} icon]
  [:<>
   (when assigned?
     [:span.role-assigned [assigned-icon] "Assigned"])
   [:span.role-glyph [icon]]])

(defn- role
  [{:keys [assigned?] :as row} icon]
  [role-mark {:class (when assigned? "assigned")
              :title (role-title row)}
   [role-glyphs row icon]])

(defn- role-menu-head
  [{:keys [person] :as row}]
  [menu-head
   (when (or (not person) (not-empty (:user/full-name person)))
     [:span.menu-head-name (row-label row)])
   (when person
     [:span.menu-head-detail (data/assignee-email person)])])

(defn role-items
  "The roles a share can hold as a menu's items, the one holding `permission`
   checked. `on-change` gets the permission of another one picked."
  [{:keys [permission on-change]}]
  [c/DropdownMenuRadioGroup
   {:value (name permission)
    :onValueChange (fn [picked]
                     (when (not= picked (name permission))
                       (on-change (keyword "permission.type" picked))))}
   (for [{role-permission :permission role-icon :icon} share-roles]
     ^{:key role-permission}
     [c/DropdownMenuRadioItem {:value (name role-permission)}
      [dropdown-menu/menu-item-content role-icon
       (sharing.components/permission-label role-permission)]])])

(defn- role-menu
  "A share's role as the button opening its menu: who the share is with, the
   roles it can hold, and taking it away."
  [{:keys [permission assigned?] :as row} icon {:keys [on-role on-remove]}]
  ;; Non-modal, as the urgency menu is: a modal one locks the page's scroll,
  ;; the host page's in the extension.
  [c/DropdownMenuRoot {:modal false}
   [c/DropdownMenuTrigger
    [:button {:type "button"
              :class (str role-mark " pressable" (when assigned? " assigned"))
              :title (role-title row)
              :aria-label (str "Role: " (role-title row))}
     [role-glyphs row icon]]]
   [c/DropdownMenuContent
    {:align "end"
     :class (str urgency-menu/menu-content)
     :onFocusOutside (fn [^js e] (.preventDefault e))
     :onCloseAutoFocus (fn [^js e] (.preventDefault e))}
    [role-menu-head row]
    [c/DropdownMenuSeparator]
    [role-items {:permission permission
                 :on-change #(on-role row %)}]
    [c/DropdownMenuSeparator]
    [c/DropdownMenuItem {:color "red"
                         :onSelect #(on-remove row)}
     [dropdown-menu/menu-item-content icons/outlined-close "Remove access"]]]])

(defn- row
  [{row-key :key :keys [person scope permission me?] :as row-data} icon {:keys [can-share?] :as share-tab}]
  [share-row {:data-share-key row-key
              :class (when (= row-key @(<s [::flashed])) "flash")}
   (if person
     [bubble/assignee-avatar {:assignee person :me? me?}]
     [related/tile {:class "link" :title (:hint (audience scope))} [icons/outlined-public]])
   [:span.share-row-name
    (cond-> (row-label row-data)
      me? (str " - Yourself"))]
   (if (and can-share? (not= people/owner permission))
     [role-menu row-data icon share-tab]
     [role row-data icon])])

(defn groups
  "The rows of `share-tab`, a [[task-sharing]] or a [[draft-sharing]], as the
   tray's groups, one a role under its heading, the owner's first. Within a
   role an audience comes ahead of people, then the assignee, then the rest by
   name. While the viewer may share, each share's role opens its menu."
  [{:keys [rows] :as share-tab}]
  (let [by-permission (group-by :permission rows)]
    (for [{:keys [permission heading icon]} roles
          :let [role-rows (sort-by row-order (get by-permission permission))]
          :when (seq role-rows)]
      {:label heading
       :icon [icon]
       :body (into [share-rows]
                   (for [role-row role-rows]
                     ^{:key (:key role-row)}
                     [row role-row icon share-tab]))})))
